import {useEffect, useRef} from 'react'
import {documentToHtmlString} from '@contentful/rich-text-html-renderer'
import {resolveGenericContentCta, type GenericContent} from '../../../../../../brand/lib/types/contentful'
import {animateAssetZoom} from '../../_utils/animations'

import {Heading, Prose} from '@primer/react-brand/esm'
import {CtaButtons} from '../../_shared/CtaButtons/CtaButtons'
import {Label} from '../../_shared/Label/Label'
import {Spacer} from '../../_shared/Spacer/Spacer'
import {MediaAsset} from '../../_shared/MediaAsset/MediaAsset'

import Styles from './River.module.css'

type Props = {
  content: GenericContent
  location: string
  sectionIndex: number
}

export function River(props: Props) {
  const {content, location, sectionIndex} = props
  const {htmlId, heading, subheading, label, text, media} = content.fields

  const direction = htmlId || 'left' // Used for setting left/right alignment

  const title = heading

  const eyebrow = label?.fields.text
  const description = text ? documentToHtmlString(text) : ''

  const ctaGroup = resolveGenericContentCta(content, {legacyFallback: 'first'})
  const ctaNote = subheading ? documentToHtmlString(subheading) : ''

  // Animation
  const wrapperRef = useRef<HTMLDivElement>(null)
  const assetRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const elements: Element[] = []
    if (assetRef.current) elements.push(assetRef.current)
    return wrapperRef.current ? animateAssetZoom(elements, wrapperRef.current) : undefined
  }, [])

  return (
    <div className={Styles.River} data-direction={direction}>
      <div className={Styles.Cell}>
        <div className={Styles.Wrapper}>
          {eyebrow && <Label text={eyebrow} />}

          <div className={Styles.Content}>
            {title && (
              <Heading data-heading="2" as={sectionIndex === 0 ? 'h2' : 'h3'}>
                {title}
              </Heading>
            )}

            {description && <Prose className={Styles.Description} html={description} />}

            {ctaGroup && (
              <>
                <Spacer size="48px" size1280="0" />
                <div>
                  <CtaButtons contentfulContent={ctaGroup} location={location} />
                  {ctaNote && <Prose className={Styles.CtaNote} html={ctaNote} />}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className={Styles.Cell} ref={wrapperRef}>
        {media && (
          <div ref={assetRef} data-animate="container">
            <div data-animate="item">
              <MediaAsset content={media} location={location} />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
